import React from 'react';
import styled from 'styled-components';
import { useBlockchain } from '../../contexts/BlockchainContext';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SyncHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const SyncTitle = styled.h4`
  color: white;
  margin: 0;
  font-size: 1.1rem;
`;

const SyncIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.synced ? '#4ade80' : '#fbbf24'};
  animation: ${props => props.synced ? 'none' : 'pulse 2s infinite'};
`;

const StatusText = styled.span`
  color: ${props => props.synced ? '#4ade80' : '#fbbf24'};
  font-weight: 600;
  font-size: 0.9rem;
`;

const SyncProgress = styled.div`
  margin-bottom: 15px;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4ade80, #22c55e);
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const SyncDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
`;

const DetailItem = styled.div`
  text-align: center;
`;

const DetailLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const DetailValue = styled.div`
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
`;

const LastSyncTime = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  text-align: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SyncStatus = () => {
  const { syncStatus, blockHeight, peers, isConnected } = useBlockchain();

  const getSyncPercentage = () => {
    if (!syncStatus || !syncStatus.syncing) return 100;
    return syncStatus.percentage || 0;
  };

  const isSynced = () => {
    return isConnected && (!syncStatus?.syncing || getSyncPercentage() >= 100);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <Container>
      <SyncHeader>
        <SyncTitle>🔗 Blockchain Sync</SyncTitle>
        <SyncIndicator>
          <StatusDot synced={isSynced()} />
          <StatusText synced={isSynced()}>
            {isSynced() ? 'Synced' : 'Syncing...'}
          </StatusText>
        </SyncIndicator>
      </SyncHeader>

      <SyncProgress>
        <ProgressLabel>
          <span>Sync Progress</span>
          <span>{getSyncPercentage().toFixed(1)}%</span>
        </ProgressLabel>
        <ProgressBar>
          <ProgressFill percentage={getSyncPercentage()} />
        </ProgressBar>
      </SyncProgress>

      <SyncDetails>
        <DetailItem>
          <DetailLabel>Current Block</DetailLabel>
          <DetailValue>{formatNumber(blockHeight || 0)}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Connected Peers</DetailLabel>
          <DetailValue>{peers || 0}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Network Status</DetailLabel>
          <DetailValue style={{ color: isConnected ? '#4ade80' : '#ef4444' }}>
            {isConnected ? 'Online' : 'Offline'}
          </DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Sync Speed</DetailLabel>
          <DetailValue>
            {syncStatus?.syncing ? '~2.5 blocks/sec' : 'N/A'}
          </DetailValue>
        </DetailItem>
      </SyncDetails>

      <LastSyncTime>
        Last sync: {formatTime(syncStatus?.lastSync || new Date())}
      </LastSyncTime>
    </Container>
  );
};

export default SyncStatus; 